import React, {useEffect, useState} from "react";
import { useNavigate } from 'react-router';
import TabMenu from "../../components/TabMenu/TabMenu"
import { useTranslation } from 'react-i18next';
import Api from "../../services/Api";
import {BookedMeeting, StrMap} from "../../interfaces/common";
import {
  dateFormatHourMinute,
  getMonthShort,
  calculateEndTime,
  todayStr,
  formatYYYYDDMM, minutesToTime
} from "../../helpers/dateFunctions";
import iconClock from "url:shared-assets/icons/clock.svg";
import iconClose from "url:shared-assets/icons/close.svg"
import iconPencil from "url:shared-assets/icons/pencil-bare.svg"
import iconCalendar from "url:shared-assets/icons/calendar.svg"
import DotsPopup from "../../components/Popup/DotsPopup";
import {Modal, ModalBody, ModalHeader} from "../../components/Modal";
import CustomButton from "../../components/CustomButton";
import CancelMeeting from "../../components/CancelMeeting/CancelMeeting";
import BookMeetingFree from "../../components/BookMeeting/BookMeetingFree";
import {openMeeting} from "../../helpers/open_meeting"
import DatePicker from "../../components/DatePicker";

const statusBillable = ['default', 'zebrain_confirmed', 'coach_confirmed', 'client_cancelled_late',
  'client_missed']
const statusName:StrMap = {
  "default": "Ok",
  "zebrain_confirmed": "Zebrain confirmed",
  "zebrain_cancelled": "Zebrain cancelled",
  "coach_confirmed": "Coach confirmed",
  "coach_missed": "Coach missed",
  "coach_cancelled": "Coach cancelled",
  "client_cancelled": "Client cancelled",
  "client_cancelled_late": "Client cancelled late",
  "client_missed": "Client missed",
}

export default function CoachBookings() {
  const api = Api.getInstance();
  const translation = useTranslation();
  const { t, i18n } = translation;
  const lang = i18n.language ? i18n.language.startsWith('sv') ? 'sv' : 'en' : 'en'
  const navigate = useNavigate();
  const [dayMeetings, setDayMeetings] = useState<Record<string, Array<BookedMeeting>>>({});
  const [view, setView] = useState<string>("coming");
  const [rawMeetings, setRawMeetings] = useState<Array<BookedMeeting>>([])
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [selectedMeeting, setSelectedMeeting] = useState<BookedMeeting|null>(null)
  const [showCancelMeeting, setShowCancelMeeting] = useState<boolean>(false)
  const [showChangeMeeting, setShowChangeMeeting] = useState<boolean>(false)
  const [selectDate, setSelectDate] = useState<string>("")
  const [fromDate, setFromDate] = useState<Date | null>(null)
  const [toDate, setToDate] = useState<Date | null>(null)
  const [editMeeting, setEditMeeting] = useState<BookedMeeting|null>(null)
  const now = new Date()
  let thisDate = ""
  let prevDate = ""
  let today = todayStr()

  useEffect(() => {
    getData()
  }, [])

  const getData = () => {
    api.get('coach/meetings?all=1')
      .then(response => {
        let theDayMeetings: Record<string, Array<BookedMeeting>> = {}
        let meetingList: Array<BookedMeeting> = []
        for (const m of response.json.meetings) {
          const dateStr = m.start.substring(0, 10)
          let dayItem = theDayMeetings[dateStr]
          if (dayItem === undefined) {
            dayItem = []
            theDayMeetings[dateStr] = dayItem
          }
          m.startDate = new Date(m.start)
          dayItem.push(m)
          meetingList.push(m)
        }
        setDayMeetings(theDayMeetings)
        setRawMeetings(meetingList)
      })
  }

  const updateMeetingStatus = (newStatus: string) => {
    if (!editMeeting) return
    if (editMeeting.status === newStatus) return
    const data = {
      id: editMeeting.id,
      status: newStatus
    }
    api.post('coach/meeting/edit_status', data).then(response => {
      if (response.json.status === 'ok') {
        const newMeetings = [...rawMeetings]
        for (const m of newMeetings) {
          if (m.id === editMeeting.id) {
            m.status = newStatus
          }
        }
        setRawMeetings(newMeetings)
        setEditMeeting(null)
      } else if (response.json.error) {
        alert(response.json.error)
      }
    })
  }

  const showDate = (d: Date, idx: number)=> {
    thisDate = d.getDate() + " " + getMonthShort(d.getMonth(), lang)
    const sameDate = thisDate === prevDate
    let dayStr = todayStr(d)
    prevDate = thisDate
    if (sameDate) {
      return (<></>)
    }
    return (
      <>
        {
          idx > 0 && <hr className="border-neutral-600 my-4" />
        }
        <div className="font-medium">{dayStr === today ? "Idag" : thisDate}</div>
      </>

    )
  }

  const clickChangeMeeting = (meeting: BookedMeeting) => {
    setSelectedMeeting(meeting)
    setShowChangeMeeting(true)
  }

  const clickCancelMeeting = (meeting: BookedMeeting) => {
    setSelectedMeeting(meeting)
    setShowCancelMeeting(true)
  }

  const closeCancelMeeting = (changed: boolean) => {
    setShowCancelMeeting(false)
    if (changed) {
      getData()
    }
  }

  const closeChangeMeeting = (changed: boolean) => {
    setShowChangeMeeting(false)
    if (changed) {
      getData()
    }
  }

  const goToMeeting = (meetingId: string) => {
    if (api.apiToken) {
      openMeeting(meetingId, api.apiToken, "coach");
    }
  }

  const showMeetingLine = (meeting: BookedMeeting, dayIndex: number, meetingIndex: number) => {
    let popItems: Array<any>;
    if (view === "coming") {
      popItems = [
        {title: t('to-meeting'), click: () => goToMeeting(meeting.id)},
        {title: t('change-meeting'), click: () => clickChangeMeeting(meeting)},
        {title: t('cancel-meeting'), click: () => clickCancelMeeting(meeting)}
      ]
    } else {
      popItems = [
        {title: t('to-meeting'), click: () => goToMeeting(meeting.id)},
        {title: t('summary'), click: () => navigate('/clients/' + meeting.clientId + '/meeting_summary/' + meeting.id)},
      ]
    }

    if (view === "coming" && dayIndex === 0 && meetingIndex === 0 && todayStr(meeting.startDate) === today) {
      return (
        <>
          <div className="text-xl z-link mt-4">{meeting.firstName} {meeting.lastName}</div>
          <div className="flex justify-between items-center mt-4">

            <div className="flex items-center w-36">
              <img alt="Time" src={iconClock} className="picture-xs"/>
              <span className="ml-2 text-sm font-medium">
                {dateFormatHourMinute(meeting.startDate)}
                &ndash;
                {dateFormatHourMinute(calculateEndTime(meeting.startDate, meeting.length))}
              </span>
            </div>
            <div className="w-36 flex items-center cursor-pointer" onClick={() => setEditMeeting(meeting)}>
              <img src={iconPencil} alt="edit"/>
              <p className="ml-2">{statusName[meeting.status]}</p>
            </div>

            <DotsPopup items={popItems}/>
          </div>
          <CustomButton
            onClick={() => goToMeeting(meeting.id)}
            className="w-full mt-4"
            buttonType="outlineWhiteBase"
          >
            Till möte
          </CustomButton>
        </>
      )
    }
    return (
      <>
        <div className="flex justify-between items-center mt-2">

          <div className="flex items-center">
            <div className="text-sm z-link w-32"
                 onClick={() => navigate(`/clients/${meeting.clientId}`)}>
              {meeting.firstName} {meeting.lastName}
            </div>
            <div className="flex items-center ml-2 w-36">
              <img className="ml-2 picture-xs" alt="Time" src={iconClock}/>
              <span className="ml-2 text-sm font-medium">
                {dateFormatHourMinute(meeting.startDate)}
                &ndash;
                {dateFormatHourMinute(calculateEndTime(meeting.startDate, meeting.length))}
              </span>
            </div>

          </div>
          <div className="w-36 flex items-center cursor-pointer" onClick={() => setEditMeeting(meeting)}>
            <img src={iconPencil} alt="edit"/>
            <p className="ml-2">{statusName[meeting.status]}</p>
          </div>

          <DotsPopup items={popItems}/>
        </div>
      </>
    )
  }

  if (showChangeMeeting && selectedMeeting) {
    return (
      <>
        <BookMeetingFree meeting={selectedMeeting} clientId={selectedMeeting.clientId} close={closeChangeMeeting}
                     lang={lang}/>
      </>
    )
  }

  const meetingFilter = (m: BookedMeeting) => {
    if (view === "coming") {
      return m.startDate >= now;
    } else {
      if (m.startDate > now) return false
    }

    if (filterStatus === 'billable') {
      if (statusBillable.indexOf(m.status) < 0) {
        return false
      }
    }

    if (fromDate && m.startDate < fromDate) return false
    if (toDate && m.startDate > toDate) return false

    return true
  }

  const getMatchingMeetings = () => {
    return rawMeetings.filter(m => meetingFilter(m)).length
  }

  const showSummary = () => {
    let meetingNr = 0
    let meetingMinutes = 0
    rawMeetings.filter(m => meetingFilter(m)).map(m => {
      meetingNr += 1
      meetingMinutes += m.length
    })

    return (
      <div className="mt-6 rounded bg-neutral-700 p-4">
        <div className="font-medium">
          {lang === 'sv' ? 'Summering:' : 'Summary:'}&nbsp;
          {meetingNr}&nbsp;
          {lang === 'sv' ? 'möten' : 'meetings'},&nbsp;
          {minutesToTime(meetingMinutes)}&nbsp;
          {lang === 'sv' ? 'mötestid' : 'meeting time'}
        </div>
      </div>
    )

  }

  const showMeetings = () => {
    let meetingList = Object.keys(dayMeetings).filter(day => (
      view === "coming" ?
        day >= today :
      day <= today)
    )
    // let meetingList = Object.keys(dayMeetings).filter(m => meetingFilter(m))

    if (getMatchingMeetings() === 0) {
      let message: string
      if (view === "coming") {
        message = lang === 'sv' ? 'Du har inga möten inbokade.' : 'You don\'t have any meetings scheduled.'
      } else {
        message = lang === 'sv' ? 'Du har inte haft några möten än.' : 'You haven\'t had any meetings yet.'
      }
      return <div className="mt-6">{message}</div>
    }

    return (
      <>
        <h2 className="text-lg font-medium text-white mt-4 desktop:mt-6">
          {
            view === "coming" ? t('booked-meetings') : t('earlier')
          }
        </h2>
        <div className="rounded bg-neutral-700 p-4 mt-2">
          {
            meetingList.sort((a, b) => {
              if (view === "coming") {
                return (a > b) ? 1 : -1
              } else {
                return (b > a) ? 1 : -1
              }
            }).map(((day, dayIndex) => {
                const meetings = dayMeetings[day]
                return (
                  <div key={day}>
                    <>
                      {
                        meetings.filter(m => meetingFilter(m))
                          .sort((a, b) => {
                            return (a.startDate > b.startDate) ? 1 : -1
                          })
                          .map((meeting, meetingIndex) => (
                            <div key={meeting.id}>
                              {showDate(meeting.startDate, dayIndex)}
                              {showMeetingLine(meeting, dayIndex, meetingIndex)}
                            </div>
                          ))
                      }
                    </>
                  </div>
                )
              }
            ))
          }
        </div>
      </>
    )
  }

  const setFilterDate = (d: Date | null) => {
    if (selectDate === "from") {
      if (d) d.setHours(0)
      setFromDate(d)
    } else {
      if (d) d.setHours(23)
      setToDate(d)
    }
    setSelectDate("")
  }

  const handleEditMeeting = () => {
    if (!editMeeting) return null
    console.log(editMeeting.status)
    const editable = ['default', 'coach_confirmed', 'coach_missed', 'coach_cancelled', 'client_missed']
    if (editable.indexOf(editMeeting.status) > -1) {
      return (
        <div>
          {
            editable.map((e, index) => (
            <div className="flex mt-2 cursor-pointer" key={"edit_status" + index}
                 onClick={() => updateMeetingStatus(e)}>
              <div className="z-radiobox">
                {
                  e === editMeeting.status &&
                  <div className="z-radiobox-selected"/>
                }
              </div>
              <div className="ml-2">{statusName[e]}</div>
            </div>
            ))
          }
        </div>
      )
    } else {
      return (
        <div>
          {lang === 'sv' ? 'Du kan inte ändra status ' : 'You cannot change status'}
          {statusName[editMeeting.status]}.&nbsp;
        </div>
      )
    }

  }

  return (
    <div className="main-content">
      {
        (showCancelMeeting && selectedMeeting) &&
          <CancelMeeting lang={lang} meeting={selectedMeeting}
                         close={closeCancelMeeting}/>
      }
      <h1 className="text-2xl font-medium text-white  my-4 desktop:my-6">
        {t('my-bookings')}
      </h1>
      <TabMenu items={[
        {title: t('coming'), callback: () => setView('coming')},
        {title: t('earlier'), callback: () => setView('previous')},
      ]} active={0}
      />

      {
        view === "previous" &&
          <div className="mt-6 flex items-center">

              <div className="flex items-center cursor-pointer" onClick={() => setFilterStatus("all")}>
                  <div className="z-radiobox">
                    {filterStatus === "all" && <div className="z-radiobox-selected"/>}
                  </div>
                  <p className="ml-2">{lang === 'sv' ? 'Alla' : 'All'}</p>
              </div>

              <div className="ml-8 flex cursor-pointer" onClick={() => setFilterStatus("billable")}>
                  <div className="z-radiobox">
                    {filterStatus === "billable" && <div className="z-radiobox-selected"/>}
                  </div>
                  <p className="ml-2">{lang === 'sv' ? 'Debiterbara' : 'Billable'}</p>
              </div>

              <p className="ml-4">{lang === 'sv' ? 'mellan' : 'between'}</p>
              <div className="ml-2 cursor-pointer flex" onClick={() => setSelectDate("from")}>
                  <img src={iconCalendar} alt="edit"/>
                  <div className="ml-2 w-24">
                    {fromDate ? formatYYYYDDMM(fromDate) : '____-__-__'}
                  </div>
              </div>


              <p className="ml-2">{lang === 'sv' ? 'och' : 'and'}</p>
              <div className="ml-2 cursor-pointer flex" onClick={() => setSelectDate("to")}>
                  <img src={iconCalendar} alt="edit"/>
                  <div className="ml-2 w-24">
                    {toDate ? formatYYYYDDMM(toDate) : '____-__-__'}
                  </div>
              </div>
          </div>
      }


      {
        selectDate !== "" &&
          <div className="mt-4 border border-neutral-800 bg-neutral-700">
              <div className="flex justify-between w-full p-4">
                  <h2 className="text-xl text-center">
                    {
                      selectDate === "from" ? (
                        <span>{lang === 'sv' ? 'Från och med' : 'From'}</span>
                      ) : (
                        <span>{lang === 'sv' ? 'Till och med' : 'To date'}</span>
                      )
                    }
                  </h2>
                  <p className="text-xl cursor-pointer" onClick={() => setFilterDate(null)}>
                      [{lang === 'sv' ? 'Rensa' : 'Clear'}]
                  </p>
                  <img src={iconClose} onClick={() => setSelectDate("")} alt="close" className="cursor-pointer"/>
              </div>
              <DatePicker dates={[]} clickDate={(d: Date) => setFilterDate(d)} preSelectedDate={null}
                          skipClickMonthChange={true}/>
          </div>
      }

      {showMeetings()}

      {showSummary()}

      {
        editMeeting &&
          <Modal isOpen={true} onClose={() => setEditMeeting(null)}>
              <ModalHeader onClose={() => setEditMeeting(null)}>
              <p>
                  {lang === 'sv' ? 'Status för möte' : 'Status for meeting'}
                    &nbsp;
                  {formatYYYYDDMM(editMeeting.startDate)}&nbsp;
                  {dateFormatHourMinute(editMeeting.startDate)}
                </p>
            </ModalHeader>
            <ModalBody>
              {handleEditMeeting()}
            </ModalBody>
        </Modal>
      }

    </div>
  );
};